/**
 * Content definition types — the single schema source (AD-028).
 *
 * Phase 4 (P4.1) freezes the content contract: the per-category models below
 * and the validators in validate.ts are the shape every later sub-phase and
 * all Phase 10 AI generation build on. Changing a model's shape after this is
 * a Fable-gated escalation (MODEL_ASSIGNMENT_MATRIX.md, ROADMAP_BREAKDOWN.md
 * P4.1). Field semantics: docs/design/content-schema.md.
 *
 * Design grammar encoded here is sourced from the approved Phase 1/2 docs:
 * crafting.md (four-roll outcome, recipe lifecycle), combat.md (six behaviour
 * archetypes, telegraph floors, the AD-021 power envelope, stats), and
 * world-structure.md (biome node/enemy tables, tier rules, shrines), plus the
 * bound economy decisions AD-039 (scarcity ceiling) and AD-041 (pity disabled).
 */
/** Mirrors the directories under content/ (content/README.md). */
export const CONTENT_CATEGORIES = [
    'appearance-options',
    'asset-packs',
    'biomes',
    'cosmetics',
    'dungeons',
    'enemies',
    'factions',
    'items',
    'navmeshes',
    'npcs',
    'quests',
    'raids',
    'recipes',
    'resources',
    'terrain-tiles',
    'themes',
];
// ---------------------------------------------------------------------------
// Shared design vocabulary (Phase 1, binding for all future content).
// ---------------------------------------------------------------------------
export const QUALITY_GRADES = ['crude', 'standard', 'fine', 'superior', 'masterwork'];
/** Stat multipliers per quality grade (crafting.md four-roll, roll 1). Fixed vocabulary. */
export const QUALITY_MULTIPLIERS = {
    crude: 0.9,
    standard: 1.0,
    fine: 1.08,
    superior: 1.16,
    masterwork: 1.25,
};
export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
export const ZONE_TIERS = [0, 1, 2, 3];
// ---------------------------------------------------------------------------
// Player appearance / customisation (D-6; AD-137, DR-019;
// docs/systems/appearance-customisation.md). Two grammars BY DESIGN: the body
// BUILD is a closed code enum (exactly the rig's three morph targets —
// AVD-040/041), while the wardrobe (face/hair/skin) is the data-driven
// `appearance-options` content category so it grows post-launch with no code
// change (AVD-004, Pillar 7). Appearance is cosmetic + power-neutral (AD-021):
// it never reaches the simulation or the body-invariant collider (AD-031).
// ---------------------------------------------------------------------------
/** The three fixed body builds (TGC-CHR-027, AVD-040/041). Closed: one rig morph target each — a new build is a new rig morph + an owner decision, never content. */
export const BODY_TYPES = ['slight', 'standard', 'sturdy'];
/** Customisation slots layered on the shared rig (CHR-028 face / CHR-029 hair / CHR-030 skin). */
export const APPEARANCE_SLOTS = ['face', 'hair', 'skin'];
/** Default look used when a creation request omits or partially specifies appearance. Every id is a real catalogue entry, so a defaulted character is always renderable. */
export const DEFAULT_APPEARANCE = {
    bodyType: 'standard',
    face: 'face-01',
    hair: 'hair-short',
    skin: 'skin-medium',
};
/** Narrowing guard for untrusted creation input. */
export function isBodyType(value) {
    return typeof value === 'string' && BODY_TYPES.includes(value);
}
/** Resource quality grades run 1–5 (docs/design/crafting.md). */
export const RESOURCE_QUALITY_MIN = 1;
export const RESOURCE_QUALITY_MAX = 5;
/** Faction reputation ranks (player-progression.md §5). */
export const REPUTATION_RANKS = [
    'stranger',
    'acquainted',
    'trusted',
    'honoured',
    'exemplar',
];
/** Crafting disciplines (crafting.md). Each has independent mastery 1–50. */
export const CRAFTING_DISCIPLINES = [
    'smithing',
    'leatherworking',
    'alchemy',
    'woodworking',
    'engineering',
    'furnishing',
];
/** Crafting station types. Closed vocabulary: a new station is a deliberate code change (content-pipeline.md grammar discipline). */
export const CRAFTING_STATIONS = [
    'forge',
    'tanning-rack',
    'alchemy-table',
    'workbench',
    'engineering-bench',
    'furnishing-station',
];
/** Which station each discipline uses (validated for consistency). */
export const DISCIPLINE_STATION = {
    smithing: 'forge',
    leatherworking: 'tanning-rack',
    alchemy: 'alchemy-table',
    woodworking: 'workbench',
    engineering: 'engineering-bench',
    furnishing: 'furnishing-station',
};
export const CRAFTING_MASTERY_MIN = 1;
export const CRAFTING_MASTERY_MAX = 50;
/** Gathering masteries (player-progression.md §3). The skill that yields a resource. */
export const GATHERING_SKILLS = [
    'forestry',
    'mining',
    'herbalism',
    'hunting',
    'salvaging',
];
/**
 * Resource-node → asset-id presentation convention (DR-020 / AD-141, PROCGEN-P3).
 *
 * A spawned resource node selects its placeholder mesh from the gathering skill of
 * its resource: a biome binds ONE node kit (`BiomeDef.generation.nodeKit`) and each
 * node renders as `node-<archetype>` within it. Four archetypes (flora/mineral/herb/
 * salvage) cover every gatherable, recoloured per material later (master-asset-
 * register §5; the procedural realization is `resource-node-kit-v1`). This is the
 * entity-render sibling of the scene builder's static-scatter conventions
 * (`SCATTER_ROCK_ASSET` / `MARKER_UPGRADE_ASSET`): a presentation convention, NOT a
 * frozen schema — a kit that omits an id keeps the placeholder. `hunting` is
 * intentionally ABSENT — hunting yields drop from enemies, not world nodes
 * (world-structure.md), so it has no node archetype. Pure data; no rendering is
 * wired here (the entity renderer consumes it at AP4 / Phase-11 integration).
 * Power-neutral / cosmetic (AD-021/024).
 */
export const RESOURCE_NODE_ASSET = {
    forestry: 'node-flora',
    mining: 'node-mineral',
    herbalism: 'node-herb',
    salvaging: 'node-salvage',
};
/**
 * Gathering-tool ladder (progression.md §4.3; owner fork OF-2 resolved A-002 /
 * AD-062, grammar minted AD-420). Order IS strength: a tool at a later rung
 * satisfies every earlier rung's requirement. A gathering tool is a `kind: 'tool'`
 * item that declares `gatheringSkill` + `toolRung` together (validator-enforced);
 * a `kind: 'tool'` item WITHOUT the pair (lanterns, lenses, the surveyor's staff)
 * is a utility tool and never gates a node.
 */
export const TOOL_RUNGS = ['basic', 'standard', 'advanced', 'master'];
/**
 * The highest resource tier each rung opens node access to — the §4.3 table's
 * "T2 node access prerequisite" column verbatim (Basic ⇒ T1, Standard ⇒ T2,
 * Advanced ⇒ T3; Master adds legendary-node proc eligibility, a descriptor, and
 * opens no fourth tier because `ZONE_TIERS` has none). The node REQUIREMENT is
 * derived from this table by `requiredToolRung` (sim/mastery.ts), never stored
 * per node or per resource — a resource's `tier` already says which rung it wants.
 */
export const TOOL_RUNG_NODE_ACCESS = {
    basic: 1,
    standard: 2,
    advanced: 3,
    master: 3,
};
/** Enemy behaviour archetypes (combat.md). Generic resolvers implement these once. */
export const ENEMY_ARCHETYPES = [
    'aggressor',
    'skirmisher',
    'lobber',
    'brute',
    'swarm',
    'warder',
];
/** Telegraph wind-up weight; sets the fairness floor (combat.md, networking.md). */
export const TELEGRAPH_WEIGHTS = ['standard', 'heavy'];
/** Minimum telegraph wind-up by weight, milliseconds (AD-026/AD-031 proven floors). */
export const TELEGRAPH_FLOOR_MS = {
    standard: 700,
    heavy: 1200,
};
/**
 * How an enemy attack MOVES — the presentation vocabulary that meets content and
 * rigs (AD-636). An attack names its motion; a rig ships `Attack_<motion>` (and
 * `Attack_<motion>_heavy` where it carries both weights of one motion), and the
 * client plays whichever the rig has, else its generic `Attack`. Pure
 * presentation: it never reaches the server's hit resolution, damage or timing.
 * Definitions + the rig clip contract: docs/content/raid-schema.md §3.1a.
 */
export const ATTACK_MOTIONS = [
    'slam',
    'sweep',
    'lunge',
    'snap',
    'lash',
    'charge',
    'throw',
    'burst',
];
// ---------------------------------------------------------------------------
// Ability system vocabulary (combat-depth.md §3, P7.2 — additive).
// ---------------------------------------------------------------------------
/** Weapon families that each auto-grant 2 weapon abilities (combat-depth.md §3.5). */
export const WEAPON_TYPES = ['sword', 'bow', 'staff', 'shield'];
/** Ability slot kinds (combat-depth.md §3.2). */
export const ABILITY_SLOT_KINDS = ['weapon', 'chosen'];
/** Ability targeting types (combat-depth.md §3.2). */
export const ABILITY_TARGETING_TYPES = [
    'instant',
    'skillshot',
    'area-telegraph',
    'self',
    'ally',
];
/** Soft-trinity role hints for UI/recommendation only (combat-depth.md §3.2). */
export const ABILITY_ROLE_FLAVOURS = ['striker', 'bulwark', 'mender', 'utility'];
/** Ability effect kinds — the generic vocabulary (combat-depth.md §3.3). */
export const ABILITY_EFFECT_KINDS = [
    'damage',
    'heal',
    'control',
    'buff',
    'debuff',
    'mobility',
    'shield',
    'summon',
];
/** Crowd-control subtypes (combat-depth.md §3.3 control). */
export const CONTROL_KINDS = ['root', 'slow', 'stagger', 'knockback'];
/** Mobility subtypes (combat-depth.md §3.3 mobility). */
export const MOBILITY_KINDS = ['dash', 'blink', 'leap'];
/** Damage schools (combat-depth.md §3.3 damage). */
export const DAMAGE_SCHOOLS = ['physical', 'energy'];
/** Quest objective verbs (content-pipeline.md, world-structure.md). */
export const QUEST_VERBS = ['gather', 'craft', 'kill', 'talk', 'deliver', 'visit'];
/**
 * Quest repeat cadences (`QuestDef.repeat`). `daily` = the progression.md §7.2
 * "Daily faction task": once turned in, the quest is offered again as soon as the
 * UTC day of that turn-in has passed, and its reputation reward is granted as the
 * capped `daily-task` source. A repeatable quest rewards reputation ONLY (validator
 * rule) — a repeatable Shard/item/XP/recipe reward would be a new faucet, which is
 * an economy/progression Owner-Approval-Domain change, not content.
 */
export const QUEST_REPEATS = ['daily'];
/**
 * Loot quality ladder (AD-803; docs/systems/loot-quality-ladder-2026-10.md §5) —
 * three optional, additive fields; absent, today's behaviour stands.
 * - `EnemyDef.band`: the encounter band a kill rolls its drop grade in. Absent ⇒
 *   `bossScript` ⇒ `boss`, else `trash` (§4.2).
 * - `ItemDef.sources`: where a stat item comes from (§4.6) — a `both` item's
 *   drops are weight-damped and never roll Masterwork (D3 a).
 * - `QuestReward.quality`: an authored turn-in grade (§4.5, A-096 D4a (i));
 *   never `masterwork` — a quest cannot hand out a maker's-mark grade.
 */
export const ENEMY_BANDS = ['trash', 'elite', 'boss'];
export const ITEM_SOURCES = ['craft', 'drop', 'both'];
export const QUEST_REWARD_QUALITIES = ['standard', 'fine', 'superior'];
/** Item kinds. */
export const ITEM_KINDS = [
    'weapon',
    'armor',
    'tool',
    'consumable',
    'material',
    'decoration',
    'quest',
    'container',
];
/** Equipment slots (combat.md power model; appearance-only until Phase 6 stats). */
export const EQUIPMENT_SLOTS = [
    'mainhand',
    'offhand',
    'head',
    'chest',
    'hands',
    'legs',
    'feet',
    'trinket',
];
/** Legible combat stats — no stat soup (combat.md power model). */
export const COMBAT_STATS = ['health', 'power', 'armor', 'ward'];
/** NPC roles. */
export const NPC_ROLES = ['questgiver', 'vendor', 'trainer', 'services', 'flavor'];
/**
 * NPC ambient animation sets (q-npc-ambient-animations): the presentation archetype the
 * client animates an NPC with — idle variants, a work loop or an occasional work beat, a
 * greeting and a talk loop. PRESENTATION ONLY (no gameplay, nothing on the wire); the
 * set → clip table is the client's (`frontend/src/game/npcs/ambient.ts`). Absent ⇒ the
 * client's role default (a vendor stands as a merchant, everyone else as townsfolk).
 */
export const NPC_AMBIENT_SETS = [
    'townsfolk',
    'merchant',
    'scholar',
    'smith',
    'guard',
    'herald',
    'forager',
    'lantern',
    'tavern',
    'mystic',
    'performer',
    'seated',
];
/** The hand sockets an ambient set's held prop may ride (the rig's bone sockets). */
export const NPC_AMBIENT_PROP_SLOTS = ['mainhand', 'offhand'];
/**
 * The prop table per ambient set (q-npc-props-existing-wiring, owner 2026-10-18: "we do
 * have an anvil … check those in case we already have what we need"). Data, not code: a
 * newly delivered prop (a hand torch, a basket, a tankard — `q-npc-props-missing-generation`)
 * slots in as one row here. The content validator checks every row a placed NPC uses
 * against the live items and asset packs.
 */
export const NPC_AMBIENT_PROPS = {
    // q-npc-props-adoption-and-flame (owner pick 2026-10-19): the 0.43 m smith's hammer
    // replaces the 1.6 m warhammer whose head overshot the anvil. Each grip was solved from
    // the hand bone's world frame at one phase of the set's clip (`npc-prop-fit` dev page,
    // window.__GC_FIT) and checked on the lane: the hammer's handle points down the swing with
    // the head landing on the anvil top at the strike (~0.78; the anvil stands 1.05 m out);
    // Npc_Work_Torch, Npc_Work_Basket and Npc_Work_Drink raise the LEFT hand, so those three
    // hold in the offhand: the torch stands upright when raised (0.3-0.5), the basket hangs
    // upright from the hand, and the tankard's rim meets the mouth on the drink beat (~0.45).
    smith: {
        held: [
            {
                slot: 'mainhand',
                assetPack: 'npc-hand-props-v1',
                model: 'smith-hammer',
                grip: { position: [0, 0.09, 0], rotation: [-1.0196, -1.3386, -0.7924] },
            },
        ],
        station: { assetPack: 'driftwell-town-kit-v1', model: 'anvil-station', forward: 1.05 },
    },
    lantern: {
        held: [
            {
                slot: 'offhand',
                assetPack: 'npc-hand-props-v1',
                model: 'hand-torch',
                grip: { position: [0, 0.09, 0], rotation: [2.0479, 0.5431, 2.7279] },
            },
        ],
    },
    forager: {
        held: [
            {
                slot: 'offhand',
                assetPack: 'npc-hand-props-v1',
                model: 'fruit-basket',
                grip: { position: [0, 0.09, 0], rotation: [-3.0053, 0.622, -0.2346] },
            },
        ],
    },
    tavern: {
        held: [
            {
                slot: 'offhand',
                assetPack: 'npc-hand-props-v1',
                model: 'tankard',
                grip: { position: [0, 0.09, 0], rotation: [2.3606, 0.1198, -2.7432] },
            },
        ],
    },
    // q-npc-props-seated-clips: the 0.52 m stool sits under the hips of `Npc_Sit_Chair`
    // (hips 0.67 m, thighs level). A seat at `forward` 0 is under the body, so the set must
    // not turn to face the player (the client table pins `facesPlayer: false`).
    seated: {
        station: { assetPack: 'housing-kit-v1', model: 'stool', forward: 0 },
    },
};
/**
 * Scarcity-target applicability (economy-review-2026-06-11 F-E2). Item-stock
 * is the launch semantics; resource-flow and cosmetic-ownership are documented
 * extension points so the frozen contract already scopes future targets.
 */
export const SCARCITY_SCOPES = ['item-stock', 'resource-flow', 'cosmetic-ownership'];
export const POWER_ENVELOPE = {
    0: { health: 0, power: 0, armor: 0, ward: 0, maxEffectSlots: 0 },
    1: { health: 120, power: 30, armor: 20, ward: 20, maxEffectSlots: 1 },
    2: { health: 220, power: 55, armor: 38, ward: 38, maxEffectSlots: 2 },
    3: { health: 400, power: 100, armor: 70, ward: 70, maxEffectSlots: 2 },
};
/** Effect/affix slots granted by rarity (combat.md). Bounded by the tier max too. */
export const RARITY_EFFECT_SLOTS = {
    common: 0,
    uncommon: 1,
    rare: 1,
    epic: 2,
    legendary: 2,
};
/**
 * Max per-stat roll variance a recipe may declare. (1 + v) × 1.25 ≤ 1.40 ⇒
 * v ≤ 0.12; design uses ±0.10. This is what keeps a Masterwork roll inside the
 * AD-021 envelope, so it is enforced, not advisory.
 */
export const STAT_VARIANCE_MAX = 0.12;
/** Best-in-slot multiplier bound over fresh Standard gear (AD-021 +40% ceiling). */
export const BIS_ENVELOPE_MAX = 1.4;
/**
 * Consumable cooldown groups (consumables.md §4; AD-791). `potion`: one shared
 * cooldown across the group, usable in or out of combat. `food`: no cooldown,
 * refused while in combat. The two groups are independent.
 */
export const CONSUMABLE_COOLDOWN_GROUPS = ['potion', 'food'];
/** The `potion` group's one shared cooldown (consumables.md §4; AD-791). `food` has none. */
export const CONSUMABLE_POTION_COOLDOWN_MS = 30_000;
/** Effect kinds a consumable's `use` may carry — the ability grammar's heal/buff plus `stamina`. */
export const CONSUMABLE_EFFECT_KINDS = ['heal', 'buff', 'stamina'];
/** Stats a consumable buff may raise (health is the heal effect's, never a buff). */
export const CONSUMABLE_BUFF_STATS = ['power', 'armor', 'ward'];
/**
 * The validator's ceiling on a consumable's effects (consumables.md §4; AD-791),
 * as fractions of the item tier's POWER_ENVELOPE budget. Bounded here, so
 * consumable buffs are not counted in the AD-021 fresh-gear bound. Tier 0
 * carries no combat power: food group, heal only, `tier0HealTotal` at most.
 */
export const CONSUMABLE_ENVELOPE = {
    /** A burst heal ≤ this × envelope[tier].health. */
    burstHealFraction: 0.4,
    /** A heal-over-time total ≤ this × envelope[tier].health. */
    overTimeHealFraction: 0.5,
    /** Potion buff magnitude ≤ this × envelope[tier][stat]; duration ≤ potionBuffMaxMs. */
    potionBuffFraction: 0.25,
    potionBuffMaxMs: 60_000,
    /** Food buff magnitude ≤ this × envelope[tier][stat]; duration ≤ foodBuffMaxMs. */
    foodBuffFraction: 0.1,
    foodBuffMaxMs: 600_000,
    /** Stamina restored by one use, any tier ≥ 1. */
    staminaMax: 50,
    /** Tier 0: total heal across the use's effects. */
    tier0HealTotal: 30,
};
/** Feat kinds that attune an account to a prestige line. Additive (each new kind is its own T3 check). */
export const PRESTIGE_ATTUNEMENT_KINDS = ['reputation-rank'];
// ---------------------------------------------------------------------------
// Boss mechanic grammar (combat-depth.md §4, P7.4 — additive, optional). The
// `bossScript` block below is an ADDITIVE, OPTIONAL field on the existing frozen
// EnemyDef (§7.3 schema-change discipline): default-absent, back-compatible with
// the slice's archetype enemies — NOT a reshape of any frozen field. The shapes
// freeze at P7.4; every NUMBER is a `[P7 sim]` non-binding starting point owned
// by P7.5 authoring / P7.G playtest tuning (AD-072). The composition primitive is
// the EXISTING TelegraphAttack (its floors validator-enforced, the non-negotiable
// fairness contract); a TelegraphPattern only wraps it with geometry/multiplicity/
// motion. No new combat or scarcity maths.
// ---------------------------------------------------------------------------
/** Telegraph-pattern geometry (combat-depth.md §4.3). Generic resolver per shape. */
export const TELEGRAPH_PATTERN_SHAPES = [
    'point',
    'circle',
    'cone',
    'line',
    'ring',
    'cross',
];
/** Which entity a pattern's instances aim at (combat-depth.md §4.2). */
export const PATTERN_TARGETINGS = ['random', 'currentTarget', 'fixed'];
/** Boss-phase advance triggers (combat-depth.md §4.2/§4.4). */
export const BOSS_PHASE_TRIGGER_KINDS = [
    'start',
    'healthBelowPct',
    'timeMs',
    'addsCleared',
];
/** The biomeType that marks a non-player-facing Showcase Hall (AD-594; showcase-halls.md). */
export const SHOWCASE_BIOME_TYPE = 'showcase';
/**
 * The render tiers a pack may opt its textured base into on the LOW quality profile
 * (`AssetPackDef.lowQualityBase`). The same two tiers as the frontend's `RenderTier`
 * (frontend/src/game/scene/budget.ts), which pins the two together at compile time.
 */
export const ASSET_RENDER_TIERS = ['desktop', 'mobile'];
/** The effect kinds an asset emitter may run (`AssetPackDef.emitters`). */
export const ASSET_EMITTER_KINDS = ['flame'];
// P4.1 content contract frozen here — field semantics in docs/design/content-schema.md.
//# sourceMappingURL=types.js.map